import React, { useMemo, useRef, useEffect, useState } from 'react';
import styles from './Newsletter.module.css';

const STEPS = [
  { key: 'name', label: 'Your Full Name', type: 'text' },
  { key: 'email', label: 'Email Address', type: 'email' },
  { key: 'phone', label: 'Phone Number (Optional)', type: 'tel' },
  { key: 'interest', label: 'How would you like to partner? (e.g. Prayer, Ministry, Giving)', type: 'text' },
];

export default function Newsletter() {
  const sectionRef = useRef(null);
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({ name: '', email: '', phone: '', interest: '' });
  const [submitted, setSubmitted] = useState(false);

  const active = useMemo(() => STEPS[step], [step]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          obs.unobserve(el);
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const canGoNext = step === 2 || values[active.key].trim().length > 0;

  const onSubmit = e => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section ref={sectionRef} className={styles.section} id="partner">
      <div className={styles.container}>
        {/* Unified Partner Card Container */}
        <div className={`${styles.partnerCard} reveal d2`}>
          {/* Left Side: Generated Church Partner Group Photo */}
          <div className={styles.photoSide}>
            <div
              className={styles.photoBackdrop}
              style={{ backgroundImage: 'url("/assets/images/IMG1.webp")' }}
              aria-hidden="true"
            />
            <div className={styles.imgWrapper}>
              <img
                src="/assets/images/IMG1.webp"
                alt="Faith Fellowship Church Volunteers and Partners"
                className={styles.partnerImg}
                width="1130"
                height="720"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className={styles.photoScrim} />
            <div className={styles.photoCaption}>
              <span className={styles.captionSub}>COMMUNITY &amp; OUTREACH</span>
              <p className={styles.captionHeading}>United in Faith, Serving with Purpose</p>
            </div>
          </div>

          {/* Right Side: Partnership Form and Content */}
          <div className={styles.formSide}>
            <div className={styles.header}>
              <span className="section-eyebrow">MAKE AN IMPACT</span>
              <h2 className={styles.cardTitle}>
                PARTNER WITH <em>US</em>
              </h2>
              <p className={styles.subtitle}>
                Join hands with Faith Fellowship Church to advance God's Kingdom through prayer, ministry, and outreach.
              </p>
            </div>

            <div className={styles.formWrap}>
              {submitted ? (
                <div className={styles.successBox}>
                  <div className={styles.successIcon}>✓</div>
                  <h3 className={styles.successTitle}>Thank You For Partnering!</h3>
                  <p className={styles.successText}>
                    We are blessed to connect with you. Our pastoral and ministry team will reach out to you shortly.
                  </p>
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={() => {
                      setSubmitted(false);
                      setStep(0);
                      setValues({ name: '', email: '', phone: '', interest: '' });
                    }}
                  >
                    Send Another Note
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.stepIndicator}>
                    {STEPS.map((s, idx) => (
                      <div 
                        key={s.key} 
                        className={`${styles.stepBar} ${idx <= step ? styles.activeStepBar : ''}`} 
                      />
                    ))}
                  </div>

                  <form className={styles.form} onSubmit={onSubmit}>
                    <div className={styles.inputGroup}>
                      <input
                        className={styles.input}
                        type={active.type}
                        placeholder={active.label}
                        aria-label={active.label}
                        value={values[active.key]}
                        onChange={e => setValues(v => ({ ...v, [active.key]: e.target.value }))}
                        autoFocus
                      />
                    </div>

                    <div className={styles.controls}>
                      {step > 0 && (
                        <button className={styles.btnBack} type="button" onClick={() => setStep(s => Math.max(0, s - 1))}>
                          &lt; Back
                        </button>
                      )}

                      {step < STEPS.length - 1 ? (
                        <button
                          className={styles.btnNext}
                          type="button"
                          onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
                          disabled={!canGoNext}
                        >
                          Next &gt;
                        </button>
                      ) : (
                        <button className={styles.btnNext} type="submit" disabled={!canGoNext}>
                          Submit Partnership
                        </button>
                      )}
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}